<template>
    <v-dialog v-model="dialog" max-width="480" persistent transition="dialog-bottom-transition">
        <div class="cb-dialog">
            <!-- ─────────────── HEADER ─────────────── -->
            <header class="cb-header">
                <div class="cb-header__icon">
                    <v-icon size="22" color="white">mdi-phone-lock</v-icon>
                </div>
                <div class="cb-header__title-wrap">
                    <h2 class="cb-title">חסימת חיוג</h2>
                    <span class="cb-subtitle" v-if="itemToEdit && itemToEdit.manui_number">
                        מנוי {{ itemToEdit.manui_number }}
                    </span>
                </div>
                <button type="button" class="cb-close" @click="closeDialog" aria-label="סגירה" :disabled="busy">
                    <v-icon size="20">mdi-close</v-icon>
                </button>
            </header>

            <!-- ─────────────── BODY ─────────────── -->
            <div class="cb-body">
                <!-- Loading state (initial check) -->
                <div v-if="checking" class="cb-state cb-state--loading">
                    <v-progress-circular indeterminate color="#0d2c6d" size="42" width="3"></v-progress-circular>
                    <p class="cb-state__text">בודק את סטטוס חסימת החיוג…</p>
                </div>

                <!-- Error state (initial check failed) -->
                <div v-else-if="loadError" class="cb-state cb-state--error">
                    <div class="cb-state__icon cb-state__icon--error">
                        <v-icon size="28" color="#c74747">mdi-alert-circle-outline</v-icon>
                    </div>
                    <p class="cb-state__text">לא ניתן היה לבדוק את סטטוס חסימת החיוג.</p>
                    <button type="button" class="cb-btn cb-btn--ghost" @click="fetchStatus">
                        <v-icon size="16">mdi-refresh</v-icon>
                        <span>נסה שוב</span>
                    </button>
                </div>

                <!-- Loaded state - status card + action -->
                <template v-else>
                    <div class="cb-status-card" :class="isBlocked ? 'cb-status-card--blocked' : 'cb-status-card--open'">
                        <div class="cb-status-card__icon">
                            <v-icon size="28" :color="isBlocked ? '#c74747' : '#16804b'">
                                {{ isBlocked ? 'mdi-phone-lock' : 'mdi-phone-check-outline' }}
                            </v-icon>
                        </div>
                        <div class="cb-status-card__body">
                            <span class="cb-status-card__label">סטטוס נוכחי</span>
                            <strong class="cb-status-card__value">
                                {{ isBlocked ? 'חסימת חיוג פעילה' : 'ללא חסימת חיוג' }}
                            </strong>
                        </div>
                    </div>

                    <p class="cb-hint">
                        <v-icon size="16" color="#8066f0" class="ml-1">mdi-information-outline</v-icon>
                        <span v-if="isBlocked">
                            ניתן לבטל את חסימת החיוג ולאפשר שוב שיחות יוצאות במנוי.
                        </span>
                        <span v-else>
                            ניתן להפעיל חסימת חיוג כדי לחסום שיחות יוצאות במנוי.
                        </span>
                    </p>
                </template>
            </div>

            <!-- ─────────────── FOOTER ─────────────── -->
            <footer class="cb-footer" v-if="!checking">
                <button type="button" class="cb-btn cb-btn--ghost" @click="closeDialog" :disabled="busy">
                    ביטול
                </button>

                <template v-if="!loadError">
                    <button v-if="isBlocked" type="button" class="cb-btn cb-btn--success" @click="toggleBlock"
                        :disabled="busy">
                        <v-progress-circular v-if="busy" indeterminate size="16" width="2" color="white"
                            class="ml-2"></v-progress-circular>
                        <v-icon v-else size="17" class="ml-1">mdi-phone-check</v-icon>
                        <span>ביטול חסימת חיוג</span>
                    </button>
                    <button v-else type="button" class="cb-btn cb-btn--danger" @click="toggleBlock" :disabled="busy">
                        <v-progress-circular v-if="busy" indeterminate size="16" width="2" color="white"
                            class="ml-2"></v-progress-circular>
                        <v-icon v-else size="17" class="ml-1">mdi-phone-lock</v-icon>
                        <span>הפעל חסימת חיוג</span>
                    </button>
                </template>
            </footer>
        </div>
    </v-dialog>
</template>

<script>
import ApiServices from '@/services/api.service'

export default {
    name: 'CallBlockDialog',
    props: {
        value: { type: Boolean, default: false },
        itemToEdit: { type: Object, required: true }
    },
    data() {
        return {
            checking: true,
            busy: false,
            loadError: false,
            isBlocked: false, // populated from API 1
        };
    },
    computed: {
        dialog: {
            get() { return this.value; },
            set(v) { this.$emit('input', v); }
        }
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.fetchStatus();
            }
        }
    },
    methods: {
        closeDialog() {
            if (this.busy) return;
            this.$emit('input', false);
        },
        // ── API 1: check current block status ──
        async fetchStatus() {
            this.checking = true;
            this.loadError = false;
            try {
                const token = localStorage.getItem("token");
                const number = this.itemToEdit && this.itemToEdit.manui_number;
                const api = process.env.VUE_APP_BASE_URL
                    + `/manuim/get_call_block_status?number=${encodeURIComponent(number)}`;

                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                if (res.status >= 400) {
                    this.loadError = true;
                    return;
                }
                const jsonObject = await res.json();
                if (!jsonObject || jsonObject.status !== true) {
                    this.loadError = true;
                    return;
                }
                this.isBlocked = !!jsonObject.callBlock;
            } catch (error) {
                this.loadError = true;
            } finally {
                this.checking = false;
            }
        },
        // ── API 2: toggle block (block or release) ──
        async toggleBlock() {
            this.busy = true;
            try {
                const token = localStorage.getItem("token");
                const manuiId = this.itemToEdit && this.itemToEdit._id;
                const api = process.env.VUE_APP_BASE_URL + `/manuim/toggle_call_block`;
                const body = JSON.stringify({
                    manui_id: manuiId,
                    action: this.isBlocked ? 'release' : 'block'
                });

                const res = await fetch(api, ApiServices.requestOptions("POST", body, token));
                const jsonObject = await res.json().catch(() => ({}));

                if (res.status >= 400 || !jsonObject || jsonObject.status !== true) {
                    const errText = this.isBlocked
                        ? "שגיאה בביטול חסימת החיוג"
                        : "שגיאה בהפעלת חסימת החיוג";
                    this.$emit('itemEdited', "", errText, "red");
                    return;
                }

                const successText = this.isBlocked
                    ? "חסימת החיוג בוטלה בהצלחה"
                    : "חסימת החיוג הופעלה בהצלחה";

                // Emit using the same signature as other dialogs in the system:
                // (item, text, color). Item is left empty ("") because status
                // changes do not modify the row data in the manuim table.
                this.$emit('itemEdited', "", successText, "green");
                this.$emit('input', false);
            } catch (error) {
                this.$emit('itemEdited', "", "שגיאה: " + error, "red");
            } finally {
                this.busy = false;
            }
        }
    },
    mounted() {
        // Fetch when opened via `v-if` (dialog is created only when opened)
        if (this.value) this.fetchStatus();
    }
};
</script>

<style scoped>
.cb-dialog {
    --cb-navy: #0d2c6d;
    --cb-navy-soft: #142f78;
    --cb-purple: #8066f0;
    --cb-border: #e3e8ef;
    --cb-border-soft: #eef2f7;
    --cb-text: #0f172a;
    --cb-text-soft: #475569;
    --cb-text-mute: #94a3b8;
    --cb-green: #16804b;
    --cb-green-soft: rgba(22, 128, 75, 0.10);
    --cb-red: #c74747;
    --cb-red-soft: rgba(199, 71, 71, 0.10);
    --cb-orange: #c0691c;
    --cb-ease: cubic-bezier(0.22, 1, 0.36, 1);

    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    box-shadow: 0 25px 60px -20px rgba(13, 44, 109, 0.35);
}

/* ─────────────── HEADER ─────────────── */
.cb-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
}

.cb-header__icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.20);
    display: grid;
    place-items: center;
    flex-shrink: 0;
}

.cb-header__title-wrap {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.cb-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.cb-subtitle {
    font-size: 0.78rem;
    opacity: 0.85;
    margin-top: 2px;
}

.cb-close {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    color: #fff;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.15);
    cursor: pointer;
    transition: all 0.15s var(--cb-ease);
}

.cb-close:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.20);
}

.cb-close:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ─────────────── BODY ─────────────── */
.cb-body {
    padding: 20px;
    min-height: 140px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.cb-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 24px 8px;
    text-align: center;
}

.cb-state__text {
    margin: 0;
    color: var(--cb-text-soft);
    font-size: 0.92rem;
}

.cb-state__icon--error {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: var(--cb-red-soft);
    display: grid;
    place-items: center;
}

/* Status card */
.cb-status-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border-radius: 12px;
    border: 1px solid var(--cb-border);
    background: #fbfcfe;
}

.cb-status-card--blocked {
    background: var(--cb-red-soft);
    border-color: rgba(199, 71, 71, 0.25);
}

.cb-status-card--open {
    background: var(--cb-green-soft);
    border-color: rgba(22, 128, 75, 0.25);
}

.cb-status-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: #fff;
    display: grid;
    place-items: center;
    flex-shrink: 0;
}

.cb-status-card__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cb-status-card__label {
    font-size: 0.78rem;
    color: var(--cb-text-mute);
    font-weight: 600;
}

.cb-status-card__value {
    font-size: 1rem;
    color: var(--cb-text);
    font-weight: 700;
}

.cb-hint {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 0;
    padding: 10px 12px;
    background: rgba(128, 102, 240, 0.06);
    border: 1px dashed rgba(128, 102, 240, 0.30);
    border-radius: 10px;
    color: var(--cb-text-soft);
    font-size: 0.86rem;
    line-height: 1.5;
}

/* ─────────────── FOOTER ─────────────── */
.cb-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    background: var(--cb-border-soft);
    border-top: 1px solid var(--cb-border);
}

.cb-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 9px 16px;
    border-radius: 10px;
    font-family: inherit;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.15s var(--cb-ease);
    min-height: 38px;
}

.cb-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.cb-btn--ghost {
    background: #fff;
    border-color: var(--cb-border);
    color: var(--cb-text-soft);
}

.cb-btn--ghost:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: var(--cb-text);
}

.cb-btn--danger {
    background: var(--cb-red);
    color: #fff;
    box-shadow: 0 5px 12px -4px rgba(199, 71, 71, 0.60);
}

.cb-btn--danger:hover:not(:disabled) {
    background: #b13c3c;
    box-shadow: 0 6px 14px -4px rgba(199, 71, 71, 0.75);
}

.cb-btn--success {
    background: var(--cb-green);
    color: #fff;
    box-shadow: 0 5px 12px -4px rgba(22, 128, 75, 0.60);
}

.cb-btn--success:hover:not(:disabled) {
    background: #126c3f;
    box-shadow: 0 6px 14px -4px rgba(22, 128, 75, 0.75);
}
</style>